<template>
  <div
    class="w-full flex flex-wrap lg:flex-nowrap transition-all duration-300"
    :class="[post_id === currentPost.id ? 'h-full' : 'h-64']"
  >
    <!-- Left Col -->
    <div
      :class="[
        post_id === currentPost.id
          ? 'translate-x-0 opacity-100'
          : 'opacity-0 -translate-x-8',
      ]"
      class="w-full lg:w-4/12 pr-4 pb-8 lg:pb-0 mb-8 lg:mb-0 border-b lg:border-b-0 border-bluegray-300 flex flex-col space-y-4 transform transition-all duration-500"
    >
      <!-- Not editing/public -->
      <div v-if="!editing || previewing">
        <button
          v-if="currentPost.category_title && !page"
          class="rounded bg-bluegray-500 mb-4 py-2 px-4 text-white cursor-default"
        >
          <h6>{{ currentPost.category_title }}</h6>
        </button>

        <p
          v-if="
            (currentPost.year_tag && currentPost.year_tag.length && !editing) ||
            previewing
          "
          class="text-primary font-semibold font-xs uppercase mb-2"
        >
          <span v-for="(yearTag, index) in currentPost.year_tag">
            <span v-if="index"> | </span>
            {{ yearTag }}
          </span>
        </p>

        <h2>{{ currentPost.title }}</h2>

        <!-- tags -->
        <MultipleTags
          v-if="
            currentPost.industry_tags && currentPost.industry_tags.length > 0
          "
          :tags="currentPost.industry_tags"
          :colour="isContributor ? 'text-cw-alt' : 'text-primary'"
          :titles="true"
          :flex="false"
        />
        <PostTags
          v-if="currentPost.general_tags && currentPost.general_tags.length > 0"
          :tags="currentPost.general_tags"
        />

        <!-- Publish date -->
        <div
          v-if="currentPost.created_at && currentPost.category != 3 && !page"
        >
          <p class="italic mb-8 text-bluegray-700 font-semibold text-sm">
            Published: {{ currentPost.created_at_ui }}
          </p>
        </div>

        <!-- Event Specific -->
        <div v-if="currentPost.category === 3">
          <!-- Dates -->
          <div
            v-if="currentPost.event_start_at_ui"
            class="mb-2 flex items-start justify-start text-bluegray-700 pt-12"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clip-rule="evenodd"
              />
            </svg>
            <div class="flex flex-col justify-start items-start">
              <span>{{ currentPost.event_start_at_ui }}</span>
              <span
                v-if="
                  currentPost.event_end_at_ui &&
                  currentPost.event_end_at_ui !== currentPost.event_start_at_ui
                "
              >
                <span class="text-gray-400 text-sm">to</span>
                {{ currentPost.event_end_at_ui }}
              </span>
            </div>
          </div>

          <!-- Time -->
          <div
            v-if="currentPost.event_start_at_time"
            class="mb-2 flex items-center text-bluegray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clip-rule="evenodd"
              />
            </svg>
            {{ currentPost.event_start_at_time }}
          </div>

          <!-- Venue Title -->
          <div
            v-if="currentPost.event_venue_title"
            class="mb-2 flex items-center text-bluegray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clip-rule="evenodd"
              />
            </svg>
            {{ currentPost.event_venue_title }}
          </div>

          <!-- Venue Address -->
          <div
            v-if="currentPost.event_venue_address"
            class="mb-2 flex text-bluegray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
                clip-rule="evenodd"
              />
            </svg>
            {{ currentPost.event_venue_address }}
          </div>
        </div>

        <!-- Job Specific -->
        <div v-if="currentPost.category === 2">
          <!-- Job Type -->
          <div v-if="currentPost.job_type" class="mb-2 flex text-bluegray-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clip-rule="evenodd"
              />
            </svg>
            <p class="uppercase text-sm font-semibold">
              {{ jobTypes[currentPost.job_type].name }}
            </p>
          </div>

          <!-- Job Employer -->
          <div
            v-if="currentPost.job_employer"
            class="mb-2 flex text-bluegray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                clip-rule="evenodd"
              />
            </svg>
            <p>{{ currentPost.job_employer }}</p>
          </div>
        </div>

        <!-- Resources -->
        <div
          v-if="currentPost.resources && currentPost.resources.length > 0"
          class="mt-8 space-y-4"
        >
          <h3>Attached resources</h3>
          <div class="bg-bluegray-100 space-y-1">
            <div
              v-for="(resource, i) in currentPost.resources"
              :id="resource.id"
              :key="i"
              :ref="'resource-' + i"
              class="flex text-bluegray-500 transition-all duration-100 hover:bg-bluegray-300 cursor-pointer w-full py-2 px-6"
              @click="handleResourceClick(resource)"
            >
              <svg
                v-if="resource.type === 2"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-if="resource.type === 1"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                  clip-rule="evenodd"
                />
              </svg>
              <p class="ml-4 truncate text-ellipsis w-full">
                {{ resource.title }}
              </p>
              <a
                :id="'resource:' + resource.id"
                target="_blank"
                class="hover:text-black hidden"
                :href="resource.file_url"
                >Download</a
              >
            </div>
          </div>
        </div>

        <!-- Button -->
        <a
          v-if="currentPost.link_title"
          class="mt-8 btn btn-inline btn-primary no-underline text-center"
          target="_blank"
          :href="currentPost.link_url"
        >
          {{ currentPost.link_title }}
        </a>
      </div>
    </div>
    <!-- /Left Col -->

    <!-- Right Col -->
    <div
      :class="[
        post_id === currentPost.id
          ? 'translate-x-0 opacity-100'
          : 'opacity-0 translate-x-8',
      ]"
      class="w-full lg:w-8/12 lg:border-l border-bluegray-300 lg:pl-6 transition-all duration-300 ease-in-out transform"
    >
      <div v-if="!editing || previewing">
        <img class="w-full" :src="currentPost.image_featured" />
        <h3 class="my-6">{{ currentPost.description }}</h3>
        <span class="prose" v-html="currentPost.body"></span>
      </div>
    </div>
    <!-- /Right Col -->
  </div>
</template>

<script>
import PostTags from '../../../shared/components/PostTags.vue'
import MultipleTags from '../../../shared/components/MultipleTags.vue'
import { mapGetters } from 'vuex'
import * as loadingAnimation from '../../../shared/components/loading.json'
import { JOB_TYPES } from '../../../js/util/constants.js'

export default {
  components: {
    PostTags,
    MultipleTags,
  },

  props: {
    post_id: {
      type: Number,
      required: false,
      default: 0,
    },

    page: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      thinking: false,

      editing: false,
      stateChanging: false,
      previewing: false,

      loaded: false,

      tags: {},

      jobTypes: JOB_TYPES.slice(0, -2),
    }
  },

  computed: {
    ...mapGetters({
      currentPost: 'shared/getCurrentPost',
    }),
  },

  mounted() {
    this.$set(this, 'tags', this.currentPost.tags)
  },

  methods: {
    previewPost() {
      this.$set(this, 'stateChanging', true)
      setTimeout(() => {
        this.$set(this, 'editing', false)
        this.$set(this, 'previewing', true)
        this.$set(this, 'stateChanging', false)
      }, 800)
    },

    handleResourceClick(resource) {
      let resourceref = 'resource:' + resource.id
      if (resource.type === 1) {
        // File - ToDo: create file download option
        // this.$refs.resourceref.click();
        document.getElementById(resourceref).click()
      } else if (resource.type === 2) {
        // Image
        // this.$refs.resourceref.click();
        document.getElementById(resourceref).click()
      }
    },
  },
}
</script>

<style scoped>
.tag-item::after {
  position: absolute;
  right: -0.5rem;
  top: 50%;
  content: '';
  background: currentColor;
  height: 4px;
  width: 2px;
  border-radius: 6px;
  transform: translateY(-50%);
}
</style>
